/** Confirm deletion of every workspace document owned by one connector. */
import { useState } from 'react'
import { Trash2 } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
} from '@/components/ui'

const CONFIRMATION_PHRASE = 'RESET WORKSPACE'

export function ResetWorkspaceDialog({
  open,
  sourceName,
  vaultName,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  sourceName: string
  vaultName: string
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  const [confirmation, setConfirmation] = useState('')
  const confirmed = confirmation === CONFIRMATION_PHRASE

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset {sourceName} workspace data?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <span className="block">
              This permanently deletes every document delivered by{' '}
              <strong className="font-semibold text-foreground">{sourceName}</strong> from{' '}
              <strong className="font-semibold text-foreground">{vaultName}</strong> and this
              workspace, including all analysis results. The Vault is a live view of these
              workspace documents, so the deletion removes them from both places.
            </span>
            <span className="block">
              Nothing is synced or analyzed again as part of this reset. Connector settings and
              credentials are kept. Use this only for testing or when you are certain the
              connector documents and results are no longer needed.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <label className="space-y-2 text-sm font-medium text-foreground">
          <span>
            Type <strong>{CONFIRMATION_PHRASE}</strong> to confirm.
          </span>
          <Input
            aria-label={`Type ${CONFIRMATION_PHRASE} to confirm`}
            autoComplete="off"
            value={confirmation}
            onChange={event => setConfirmation(event.target.value)}
            placeholder={CONFIRMATION_PHRASE}
          />
        </label>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!confirmed}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(event) => {
              event.preventDefault()
              onConfirm()
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Reset workspace
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
